import { MASTODON_SOCIAL_DOMAIN } from '@/constants/community'
import { getCommunityServiceBaseURL } from '@/lib/configs'
import {
  API_CHECK_USER_COMMUNITY_PERMISSION,
  API_COMMUNITY_SEARCHABLE,
  API_COMMUNITY_USER,
} from '@/services/api'
import { AccountSearchResult, getAccountsSearchV2, getPublicRoomsV3, Room } from '@/services/cms'

export const fetchCommunityAPI = <Response = Record<string, unknown>>(
  url: string,
  method: RequestInit['method'] = 'GET',
  payload: RequestInit['body'],
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken = '',
): Promise<{ ok: boolean; status: number; results: Response; headers: Headers } | void> => {
  const init: RequestInit = {
    headers: { 'content-type': 'application/json' },
    method,
    ...params,
  }

  if (htcToken) {
    init.headers = {
      ...init.headers,
      ...{ authkey: htcToken },
    }
  }
  if (payload) {
    init.body = JSON.stringify(payload)
  }
  const communityServiceBaseURL = getCommunityServiceBaseURL()
  return fetch(`${communityServiceBaseURL}${url}`, init)
    .then(async (response) => {
      const result = await response.text()
      try {
        return {
          ok: response.ok,
          status: response.status,
          results: JSON.parse(result),
          headers: response.headers,
        }
      } catch (e) {
        // Don't return json.
        return {
          ok: response.ok,
          status: response.status,
          results: result,
          headers: response.headers,
        }
      }
    })
    .catch((error) => {
      console.error('fetchCMSApiWithStatusCode error:', error)
    })
}

export const fetchCheckUserCommunityPermission = async (htcToken: string) => {
  const url = API_CHECK_USER_COMMUNITY_PERMISSION
  const res = await fetchCommunityAPI(url, 'Get', null, {}, htcToken)
  return res
}

/** Signed-in user's community account, as returned by `GET /v1/user` and `POST /v1/searchable`. */
export type CommunityUser = {
  userId: string
  userIri: string
  preferredUsername: string
  name: string
  searchable: boolean
}

/** The community fields the settings page reads; nothing else is sent to the client. */
export type SettingsCommunityUser = Pick<CommunityUser, 'name' | 'searchable'>

export function getCommunityUser(htcToken: string) {
  return fetchCommunityAPI<CommunityUser>(API_COMMUNITY_USER, 'GET', null, {}, htcToken)
}

export function postSearchable(htcToken: string, searchable: boolean) {
  return fetchCommunityAPI<CommunityUser>(
    API_COMMUNITY_SEARCHABLE,
    'POST',
    // @ts-expect-error -- helper JSON-stringifies object payloads before fetch
    { searchable },
    {},
    htcToken,
  )
}

export type PaginationData = {
  from: number
  size: number
  total: number
}

export type PublicProfileCommunityIdentity = {
  preferredUsername: string
}

export type AuthenticatedCommunityIdentity = PublicProfileCommunityIdentity & {
  htcToken: string
}

export type PublicFollowing = {
  preferredUsername: string
  name: string
  nickName: string
  domainName: string
}

export type PublicFollowingsResults = PaginationData & {
  data: PublicFollowing[]
}

type CommunityErrorResponse = {
  code: string
}

const hasCommunityV2ErrorCode = (results: unknown): results is CommunityErrorResponse =>
  typeof results === 'object' &&
  results !== null &&
  'code' in results &&
  typeof results.code === 'string'

// ⚠️ Community v2 uses a body-level `code` instead of HTTP status.
// Wiki: https://htcsense.jira.com/wiki/spaces/NEOSTORE/pages/4980965377/Community+Service+v2+API
const isCommunityV2Failure = (response: { ok: boolean; results: unknown } | void) =>
  !response || hasCommunityV2ErrorCode(response.results)

export type PublicFollowingWithIcon = PublicFollowing & {
  headIconUrl?: string
  viverse_pro?: boolean
  verified_creator?: boolean
}

export type PublicFollowingsWithIconResults = PaginationData & {
  data: PublicFollowingWithIcon[]
}

export type AuthenticatedFollowingWithRooms = PublicFollowingWithIcon & {
  publicRooms: Room[]
}

export type AuthenticatedFollowingsWithRoomsResults = PaginationData & {
  data: AuthenticatedFollowingWithRooms[]
}

export type FollowStatusData = {
  preferredUsername: string
  followersCount: number
  followingCount: number
}

export type FollowStatusResponse = {
  data: FollowStatusData[]
}

export async function postFollowStatus(
  htcToken: string | undefined,
  targetPreferredUsernameList: string[],
): Promise<FollowStatusResponse | undefined> {
  if (!targetPreferredUsernameList.length) return { data: [] }

  const batches = Array.from(
    { length: Math.ceil(targetPreferredUsernameList.length / 50) },
    (_, index) => targetPreferredUsernameList.slice(index * 50, (index + 1) * 50),
  )

  const responses = await Promise.all(
    batches.map((targetPreferredUsernameList) =>
      fetchCommunityAPI<FollowStatusResponse | CommunityErrorResponse>(
        '/api/community-service/v2/follow/status',
        'POST',
        // @ts-expect-error -- helper JSON-stringifies object payloads before fetch
        { targetPreferredUsernameList },
        {},
        htcToken,
      ),
    ),
  )

  if (responses.some(isCommunityV2Failure)) {
    return undefined
  }

  const statusByPreferredUsername = new Map(
    responses.flatMap((response) =>
      (response?.results as FollowStatusResponse).data.map((status) => [
        status.preferredUsername.toLowerCase(),
        status,
      ]),
    ),
  )

  const data = targetPreferredUsernameList.map((preferredUsername) =>
    statusByPreferredUsername.get(preferredUsername.toLowerCase()),
  )

  return data.every((status): status is FollowStatusData => status !== undefined)
    ? { data }
    : undefined
}

async function getPublicRelationship(
  identity: PublicProfileCommunityIdentity,
  relationship: 'followers' | 'following',
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsResults | undefined> {
  if (!identity.preferredUsername) return undefined

  const response = await fetchCommunityAPI<PublicFollowingsResults | CommunityErrorResponse>(
    `/api/community-service/v2/users/${encodeURIComponent(identity.preferredUsername)}/${relationship}?from=${from}&size=${size}`,
    'GET',
    null,
  )

  if (!response || isCommunityV2Failure(response)) return undefined

  return response.results as PublicFollowingsResults
}

export async function getAuthenticatedFollowings(
  identity: AuthenticatedCommunityIdentity,
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsResults | undefined> {
  if (!identity.htcToken) return undefined

  const response = await fetchCommunityAPI<PublicFollowingsResults | CommunityErrorResponse>(
    `/api/community-service/v2/followings?from=${from}&size=${size}`,
    'GET',
    null,
    {},
    identity.htcToken,
  )

  if (!response || isCommunityV2Failure(response)) return undefined

  return response.results as PublicFollowingsResults
}

export async function getAuthenticatedFollowers(
  identity: AuthenticatedCommunityIdentity,
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsResults | undefined> {
  if (!identity.htcToken || !identity.preferredUsername) return undefined

  const response = await fetchCommunityAPI<PublicFollowingsResults | CommunityErrorResponse>(
    `/api/community-service/v2/users/${encodeURIComponent(identity.preferredUsername)}/followers?from=${from}&size=${size}`,
    'GET',
    null,
    {},
    identity.htcToken,
  )

  if (!response || isCommunityV2Failure(response)) return undefined

  return response.results as PublicFollowingsResults
}

export async function getAuthenticatedFollowCounts(identity: AuthenticatedCommunityIdentity) {
  const [followings, followers] = await Promise.all([
    getAuthenticatedFollowings(identity, 0, 1),
    getAuthenticatedFollowers(identity, 0, 1),
  ])

  return {
    followingsCount: followings?.total ?? 0,
    followersCount: followers?.total ?? 0,
  }
}

async function postAuthenticatedRelationship(
  identity: AuthenticatedCommunityIdentity,
  targetPreferredUsername: string,
  relationship: 'follow' | 'unfollow',
) {
  if (!identity.htcToken || !targetPreferredUsername) return undefined

  const response = await fetchCommunityAPI<Record<string, never> | CommunityErrorResponse>(
    `/api/community-service/v2/${relationship}`,
    'POST',
    // @ts-ignore
    { targetPreferredUsername },
    {},
    identity.htcToken,
  )

  if (isCommunityV2Failure(response)) return undefined

  return response
}

export function postAuthenticatedFollow(
  identity: AuthenticatedCommunityIdentity,
  targetPreferredUsername: string,
) {
  return postAuthenticatedRelationship(identity, targetPreferredUsername, 'follow')
}

export function postAuthenticatedUnfollow(
  identity: AuthenticatedCommunityIdentity,
  targetPreferredUsername: string,
) {
  return postAuthenticatedRelationship(identity, targetPreferredUsername, 'unfollow')
}

export function getPublicFollowers(
  identity: PublicProfileCommunityIdentity,
  from: number = 0,
  size: number = 20,
) {
  return getPublicRelationship(identity, 'followers', from, size)
}

export function getPublicFollowings(
  identity: PublicProfileCommunityIdentity,
  from: number = 0,
  size: number = 20,
) {
  return getPublicRelationship(identity, 'following', from, size)
}

type GetPointBalanceResult = {
  data: {
    pointBalance: number
    userId: string
  }
}

/**
 * Get user points balance
 * @see {@link https://htcsense.jira.com/wiki/spaces/NEOSTORE/pages/3479175176/Point+Service+API+Doc#%5BGET%5D-%2Fpriv%2Fpoint-service%2Fv1%2Fpoints%2Fhistory}
 */
export async function getPointBalance(htcToken: string) {
  return fetchCommunityAPI<GetPointBalanceResult>(
    '/api/point-service/v1/points',
    'GET',
    null,
    {},
    htcToken,
  )
}

export async function getPublicFollowCounts(identity: PublicProfileCommunityIdentity) {
  const [followingsRes, followersRes] = await Promise.all([
    getPublicFollowings(identity, 0, 1),
    getPublicFollowers(identity, 0, 1),
  ])

  return {
    followingsCount: followingsRes?.total ?? 0,
    followersCount: followersRes?.total ?? 0,
  }
}

export async function getPublicFollowingsWithIcon(
  identity: PublicProfileCommunityIdentity,
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsWithIconResults> {
  const result = await getPublicFollowings(identity, from, size)

  if (!result) return { from, size: 0, total: 0, data: [] }

  return enrichFollowingsWithIcon(result)
}

async function enrichFollowingsWithIcon(
  result: PublicFollowingsResults,
): Promise<PublicFollowingsWithIconResults> {
  const communityUserIds = result.data.map(({ preferredUsername }) => preferredUsername)
  if (!communityUserIds.length) return result

  const BATCH_SIZE = 50
  let accountResults: AccountSearchResult[] = []

  if (communityUserIds.length <= BATCH_SIZE) {
    const response = await getAccountsSearchV2({ communityUserIds })
    if (response?.status === 200 && Array.isArray(response.results?.results)) {
      accountResults = response.results.results as AccountSearchResult[]
    }
  } else {
    const chunks: string[][] = []
    for (let index = 0; index < communityUserIds.length; index += BATCH_SIZE) {
      chunks.push(communityUserIds.slice(index, index + BATCH_SIZE))
    }

    const responses = await Promise.all(
      chunks.map((chunk) => getAccountsSearchV2({ communityUserIds: chunk })),
    )

    for (const response of responses) {
      if (response?.status === 200 && Array.isArray(response.results?.results)) {
        accountResults.push(...(response.results.results as AccountSearchResult[]))
      }
    }
  }

  const domainNameByCommunityUserId = new Map(
    result.data.map((following) => [
      following.preferredUsername.toLowerCase(),
      following.domainName,
    ]),
  )

  const accountByCommunityUserId = new Map(
    accountResults.map((account) => [account.community_user_id.toLowerCase(), account]),
  )

  return {
    from: result.from,
    size: result.size,
    total: result.total,
    data: result.data.flatMap((following) => {
      const account = accountByCommunityUserId.get(following.preferredUsername.toLowerCase())
      if (!account) return []

      const followingWithIcon: PublicFollowingWithIcon = {
        domainName:
          domainNameByCommunityUserId.get(following.preferredUsername.toLowerCase()) || '',
        name: account.display_name || account.community_user_id || '',
        nickName: account.display_name || '',
        preferredUsername: following.preferredUsername,
        headIconUrl: account.head_icon_url || '',
        viverse_pro: account.viverse_pro,
        verified_creator: account.verified_creator,
      }

      return followingWithIcon
    }),
  }
}

export async function getAuthenticatedFollowingsWithIcon(
  identity: AuthenticatedCommunityIdentity,
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsWithIconResults> {
  const result = await getAuthenticatedFollowings(identity, from, size)
  return result ? enrichFollowingsWithIcon(result) : { from, size: 0, total: 0, data: [] }
}

export async function getAuthenticatedFollowersWithIcon(
  identity: AuthenticatedCommunityIdentity,
  from: number = 0,
  size: number = 20,
): Promise<PublicFollowingsWithIconResults> {
  const result = await getAuthenticatedFollowers(identity, from, size)
  return result ? enrichFollowingsWithIcon(result) : { from, size: 0, total: 0, data: [] }
}

async function getAuthenticatedFollowingsWithRoomsPage(
  identity: AuthenticatedCommunityIdentity,
  totalVramKb: number,
  from: number = 0,
  size: number = 20,
): Promise<AuthenticatedFollowingsWithRoomsResults> {
  const result = await getAuthenticatedFollowings(identity, from, size)
  if (!result) return { from, size: 0, total: 0, data: [] }

  const visibleFollowings = result.data.filter(
    ({ domainName }) => !domainName.includes(MASTODON_SOCIAL_DOMAIN),
  )

  const communityUserIds = visibleFollowings.map(({ preferredUsername }) => preferredUsername)
  if (!communityUserIds.length) return { ...result, data: [] }

  const accountsResponse = await getAccountsSearchV2({ communityUserIds })
  if (accountsResponse?.status !== 200 || !Array.isArray(accountsResponse.results?.results)) {
    return { ...result, data: [] }
  }

  const accounts = accountsResponse.results.results as AccountSearchResult[]

  const accountByCommunityUserId = new Map(
    accounts.map((account) => [account.community_user_id.toLowerCase(), account]),
  )

  const followingsWithAccounts = visibleFollowings.flatMap((following) => {
    const account = accountByCommunityUserId.get(following.preferredUsername.toLowerCase())
    return account?.id ? [{ following, account }] : []
  })

  const data = await Promise.all(
    followingsWithAccounts.map(async ({ following, account }) => {
      const roomsResponse = await getPublicRoomsV3(
        { subject_id: account.id, total_vram_kb: totalVramKb },
        identity.htcToken,
      )

      return {
        domainName: following.domainName,
        name: account.display_name || account.community_user_id,
        nickName: account.display_name || '',
        preferredUsername: following.preferredUsername,
        headIconUrl: account.head_icon_url || '',
        publicRooms: roomsResponse?.status === 200 ? roomsResponse.results.results : [],
        viverse_pro: account.viverse_pro,
        verified_creator: account.verified_creator,
      }
    }),
  )

  return { ...result, data }
}

export async function getAuthenticatedFollowingsWithRooms(
  identity: AuthenticatedCommunityIdentity,
  totalVramKb: number,
  from: number = 0,
  size: number = 20,
  minimumPublicCreators: number = 1,
): Promise<AuthenticatedFollowingsWithRoomsResults> {
  const data: AuthenticatedFollowingWithRooms[] = []
  let currentFrom = from
  let total = 0

  while (currentFrom < total || total === 0) {
    const page = await getAuthenticatedFollowingsWithRoomsPage(
      identity,
      totalVramKb,
      currentFrom,
      size,
    )

    data.push(...page.data)
    total = page.total

    if (!page.size) break

    currentFrom = page.from + page.size

    const publicCreatorCount = data.filter(({ publicRooms }) => publicRooms.length > 0).length
    if (publicCreatorCount >= minimumPublicCreators) break
  }

  return { from, size: currentFrom - from, total, data }
}

export async function getAuthenticatedFollowingsList(
  identity: AuthenticatedCommunityIdentity,
): Promise<PublicFollowingWithIcon[]> {
  const followings: PublicFollowingWithIcon[] = []
  let from = 0

  while (true) {
    const page = await getAuthenticatedFollowingsWithIcon(identity, from, 50)
    followings.push(...page.data)

    const nextFrom = page.from + page.size
    if (!page.size || nextFrom >= page.total) return followings

    from = nextFrom
  }
}

export const getFollowersByRoomOwnerAccountId = async (roomData: Room | undefined) => {
  if (!roomData?.owner.community_user_id) return undefined

  return getPublicFollowers(
    {
      preferredUsername: roomData.owner.community_user_id,
    },
    0,
    1,
  )
}
